import React, {useEffect, useRef, useState } from 'react';
import Autocomplete from "react-autocomplete";
import _uniqueId from 'lodash/uniqueId';
import { Icon, Typography } from '../../index';
import "./typeahead.scss";

export const Typeahead = ({
    autoFocus,
    disabled,
    className,
    colorStyle = "neutral",
    error,
    name,
    id,
    required,
    label,
    value,
    itemList,
    hintMsg,
    icon,
    autoHighlight,
    selectOnBlur,
    loading,
    noResults,
    preOpen,
    onBlur,
    onChange,
    onFocus,
    onSelect,
    onClickPreOpen,
    onSubmitForm
}) => {

    const [enableClearBtn, setEnableClearBtn] = React.useState();
    const [hasValue, setHasValue] = React.useState(value);
    const [isFocused, setIsFocused] = React.useState(false);
    const [isEnteringSearchText, setIsEnteringSearchText] = React.useState(false);
    const [hasSelect, setHasSelect] = React.useState(false);

    let typeaheadInput = null;
    const [uId] = useState(_uniqueId('mybswh-typeahead-'));
    const fieldId = id ? id : uId;
    //const labelText = label ? `${label}${!required ? ' (Optional)' : ''}` : '';
    const labelText = label ? label : '';
    const showClearBtn = (isFocused && hasValue) || ((value || hasValue) && (enableClearBtn));
    const _colorStyle = error ? "error" : colorStyle;
    const timeoutRef = useRef();

    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    useEffect(() => {
        setHasValueStyle();
    }, [value]);

    const handleOnBlur = (event) => {
        let userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : "";
        let isSafari = (userAgent && !(/CriOS/i.test(userAgent)) && !(/FxiOS/i.test(userAgent)) && /^((?!chrome|android).)*safari/i.test(userAgent)) ? true : false;

        // Disable for Safari since autofill gets focus stuck
        if (!isSafari) {
            if (event && event.relatedTarget) {
                if (event.relatedTarget?.className !== 'mybswh-typeahead__clear-btn') {
                    hideClearBtn();
                }
            } else {
                hideClearBtn();
            }
        }
        setIsFocused(false);
        setIsEnteringSearchText(false);

        if (onBlur) {
            onBlur(event);
        }
    }

    const hideClearBtn = () => {
        clearTimeout(timeoutRef.current);
        const clearBtnTimeout = setTimeout(() => { setEnableClearBtn(false) }, 100);
        timeoutRef.current = clearBtnTimeout;
    }

    const handleOnChange = (event) => {
        setHasValueStyle();

        if (onChange) {
            onChange(event, event.target.value);
        }
    }

    const handleOnFocus = (event) => {
        setIsFocused(true);
        setEnableClearBtn(true);

        if (onFocus) {
            onFocus(event);
        }
    }

    const handleOnSelect = (val, item) => {
        if (onSelect) {
            onSelect(val, item);
        }
        setHasSelect(true);
        setIsEnteringSearchText(false);
    }

    const commitSelect = () => {
        if (typeaheadInput) {
            const highlightedIndex = typeaheadInput.state.highlightedIndex;
            const items = typeaheadInput.props.items;
            if (onSelect && highlightedIndex) {
                onSelect(items[highlightedIndex].label, items[highlightedIndex]);
            }
        }
    }

    const handleOnKeyDown = (e) => {
        if (e.key === 'Tab') {
            if (isEnteringSearchText) {
                commitSelect();
            }
            else {
                setHasSelect(false);
            }
            setIsEnteringSearchText(false);
        }
        else if (e.key === 'Enter') {
            if (isEnteringSearchText) {
                commitSelect();
            }
            else {
                if (onSubmitForm) {
                    onSubmitForm();
                }
            }
            setIsEnteringSearchText(false);
        }
        else {
            setHasSelect(false);
            setIsEnteringSearchText(true);
        }
    }

    const handleOnPaste = () => {
        setIsEnteringSearchText(true);
    }

    const handleOnClickPreOpen = (e) => {
        if (onClickPreOpen && preOpen) {
            onClickPreOpen(e);
            handleOnBlur(e);
        }
        return true;
    }

    const setHasValueStyle = () => {
        if (typeaheadInput && typeaheadInput?.refs?.input) {
            const input = typeaheadInput?.refs?.input;
            if (input.value && input.value?.length > 0) {
                setHasValue(true);
                if (isFocused) {
                    setEnableClearBtn(true);
                } else {
                    setEnableClearBtn(false);
                }
            } else {
                setHasValue(false);
                setEnableClearBtn(false);
            }
        }
    }

    const clearInput = (event) => {
        if (typeaheadInput && typeaheadInput?.refs?.input) {
            const input = typeaheadInput?.refs?.input;
            input.value = "";
            input.focus();
        }

        handleOnChange({
            event, target: {
                name: name ? name : "",
                value: ""
            }
        });
    }

    const stateStyle = {
        error: {
            iconName: 'exclamation',
            iconColor: '$color-system-b1',
            msgColor: '$color-system-b1',
        },
        neutral: {
            iconName: '',
            iconColor: '',
            msgColor: '$color-secondary-a2',
        },
        success: {
            iconName: 'check',
            iconColor: '$color-system-a1',
            msgColor: '$color-system-a1',
        },
    };

    const typeaheadClasses = [
        `mybswh-typeahead`,
        `mybswh-typeahead-color--${_colorStyle}`,
        `${className ? className : ''}`,
        `${icon ? 'mybswh-typeahead-with-icon' : ''}`,
        `${disabled ? 'mybswh-typeahead--disabled' : ''}`,
        `${isFocused ? 'mybswh-typeahead--focused' : ''}`,
        `${hasValue ? 'mybswh-typeahead--filled' : ''}`
    ].join(' ');

    const inputPropsSettings = {
        placeholder: label, className: "bsw-input", autoFocus: autoFocus,
        onFocus: (e) => handleOnFocus(e), onBlur: (e) => handleOnBlur(e),
        onKeyDown: e => handleOnKeyDown(e), onPaste: (e) => handleOnPaste(e),
        "aria-label": label,
        "data-lpignore": true,
        id: id,
        name: name,
        required: required
    };

    const openDropdown = isEnteringSearchText && (typeof value !== 'undefined' && value.length > 1);

    return (
        <>
            <div className={typeaheadClasses}>
                {icon &&
                    <div className="mybswh-typeahead__icon">
                        <Icon name={icon} size="medium" />
                    </div>
                }
                <Autocomplete wrapperStyle={{ display: 'flex' }}
                    menuStyle={{
                        borderRadius: '8px',
                        border: '1px solid rgba(221, 221, 221, 1)',
                        boxShadow: '2px 8px 10px rgba(58, 68, 85, 0.15)',
                        background: 'rgba(255, 255, 255, 1)',
                        padding: '0',
                        fontSize: '14px',
                        position: 'fixed',
                        overflow: 'auto',
                        maxHeight: '250px',
                        zIndex: '998'
                    }}
                    inputProps={inputPropsSettings}
                    items={itemList}
                    isItemSelectable={(item) => !item.category || item.label !== item.category}
                    getItemValue={item => item.id}
                    renderItem={(item, highlighted) => {
                        const itemLabel = item.category && item.label === item.category ? "suggestion-category" : "suggestion-item";
                        return (
                            <div className={itemLabel + (highlighted ? ' suggestion-highlighted' : '')} key={item.id}>
                                {item.label}
                            </div>
                        );
                    }}
                    open={openDropdown}
                    autoHighlight={autoHighlight}
                    selectOnBlur={selectOnBlur}
                    value={value}
                    ref={(instance) => (typeaheadInput = instance)}
                    onChange={e => handleOnChange(e)}
                    onSelect={(value, item) => handleOnSelect(value, item)}
                />
                {labelText && <label className="mybswh-typeahead__label" htmlFor={fieldId}>
                    <Typography
                        type={(isFocused || value || hasValue) ? "meta-01" : "body-02"}
                        color="$color-secondary-a2"
                    >
                        {labelText}
                    </Typography>
                </label>}
                <div className="mybswh-typeahead__end">
                    {showClearBtn &&
                        <button
                            type="button"
                            onClick={clearInput}
                            className="mybswh-typeahead__clear-btn"
                            aria-label={`Clear ${label}`}
                            onFocus={() => setEnableClearBtn(true)}
                            onBlur={() => setEnableClearBtn(false)}
                        >
                            <Icon name="x" color="$color-primary-a2" size="medium" />
                        </button>
                    }
                </div>
                {preOpen && !noResults && !loading && !value &&
                    <div
                        className="mybswh-typeahead__preopen-container"
                        onBlur={(e) => handleOnBlur(e)}
                        onClick={(e) => handleOnClickPreOpen(preOpen)}>
                        {preOpen}
                    </div>
                }
                {noResults && !loading && isFocused && value &&
                    <div className="mybswh-typeahead__no-results-dropdown">
                        <Typography type="body-02">
                            No results
                        </Typography>
                    </div>
                }
                {loading &&
                    <div className="mybswh-typeahead__loading-results-dropdown">
                        <div className="spinner"><div className="bounce1" /><div className="bounce2" /><div className="bounce3" /></div>
                    </div>
                }
                {hintMsg &&
                    <div className="mybswh-typeahead__hint">
                        {(_colorStyle !== 'neutral') &&
                            <div className="mybswh-typeahead__validation-icon">
                                <Icon
                                    name={stateStyle[_colorStyle]['iconName']}
                                    color={stateStyle[_colorStyle]['iconColor']}
                                    size="small"
                                />
                            </div>
                        }
                        <Typography type="meta-01" color={stateStyle[_colorStyle]['msgColor']}>
                            {hintMsg}
                        </Typography>
                    </div>
                }
            </div>
        </>
    )
}
